import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';

// Actions
import ReviewsActions from 'app/shared/actions/ReviewsActions';
import NotificationActions from 'app/shared/actions/NotificationActions';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import Constants from 'app/shared/constants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import NotificationSettingsConstants from 'app/shared/constants/NotificationSettingsConstants';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import StarRating from '@zg-rentals/multibrand-ui/lib/StarRating';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

import HeadWrapper from 'app/shared/components/HeadWrapper';
import Review from 'app/shared/components/Review';
import ReviewResponse from 'app/shared/components/ReviewResponse';
import PDPContentSkeleton from 'app/shared/components/skeletons/PDPContentSkeleton';
import SearchBox from 'app/shared/components/SearchBox';

// Selectors
import { UserSelectors, ReviewsSelectors, NotificationSelectors } from 'app/shared/selectors';

// Stylesheets
import './style.scss';

const SORT_VALUE_MAP = {
    CREATED: 'created',
    RATING: 'rating',
};

const propTypes = {
    match: PropTypes.object.isRequired,
    reviewsForListing: PropTypes.object.isRequired,
};

class ReviewsPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = {
            searchText: '',
            searchedOnce: false,
            hideReviewsWithResponses: false,
            sort: SORT_VALUE_MAP.CREATED,
            isLoadingReviews: true,
            isLoadingReviewNotifications: true,
            error: '',
        };

        this.handleSeeMore = this.handleSeeMore.bind(this);
        this.handleSubmitResponse = this.handleSubmitResponse.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleHideReviewsWithResponsesToggle = this.handleHideReviewsWithResponsesToggle.bind(this);
        this.handleReviewNotificationToggle = this.handleReviewNotificationToggle.bind(this);
        this.handleReportReview = this.handleReportReview.bind(this);
        this.handleDeleteResponse = this.handleDeleteResponse.bind(this);
        this.getReviews = this.getReviews.bind(this);
        this.getReviewNotifications = this.getReviewNotifications.bind(this);
        this.getReviewNotificationOptedOut = this.getReviewNotificationOptedOut.bind(this);
        this.renderHeader = this.renderHeader.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this.renderError = this.renderError.bind(this);
    }

    componentDidMount() {
        this.getReviews();
        this.getReviewNotifications();
    }

    handleSearch(searchText) {
        const { searchedOnce } = this.state;
        const { dispatch } = this.props;
        const newState = { searchText };
        if (!searchedOnce) {
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.SEARCH_REVIEWS));
            newState.searchedOnce = true;
        }
        this.setState(newState, this.getReviews);
    }

    handleSortChange(e) {
        const { dispatch } = this.props;
        const sort = e.target.value;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.SORT_REVIEWS, {
            label: sort,
        }));
        this.setState({ sort }, this.getReviews);
    }

    handleHideReviewsWithResponsesToggle() {
        const { hideReviewsWithResponses } = this.state;
        const { dispatch } = this.props;
        const newHideReviewsWithResponses = !hideReviewsWithResponses;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.HIDE_REVIEWS, {
            label: newHideReviewsWithResponses.toString(),
        }));
        this.setState({ hideReviewsWithResponses: newHideReviewsWithResponses }, this.getReviews);
    }

    handleReviewNotificationToggle() {
        const { dispatch } = this.props;
        const reviewNotificationOptedOut = this.getReviewNotificationOptedOut();
        const action = reviewNotificationOptedOut ? 'start' : 'stop';
        // eslint-disable-next-line no-alert
        if (confirm(`You'll ${action} receiving notifications for ALL your properties. Do you want to continue?`)) {
            this.setState({ isLoadingReviewNotifications: true });
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.NOTIFICATION_REVIEWS, {
                label: reviewNotificationOptedOut.toString(),
            }));
            const settings = [{
                deviceGroup: NotificationSettingsConstants.DEVICE_GROUP.RM_EMAIL,
                typeGroup: NotificationSettingsConstants.TYPE_GROUP.RM_REVIEW,
                optedOut: !reviewNotificationOptedOut,
            }];
            return dispatch(NotificationActions.updateSettings(settings))
                .then(() => dispatch(NotificationActions.getSettings()))
                .then(() => this.setState({ isLoadingReviewNotifications: false }))
                .catch(() => this.setState({ isLoadingReviewNotifications: false }));
        }
        return false;
    }

    handleSubmitResponse(reviewId, reviewType, message, reviewResponseId = '') {
        const { dispatch } = this.props;
        const params = {
            reviewId,
            reviewType,
            message,
        };
        if (reviewResponseId !== '') {
            params.reviewResponseId = reviewResponseId;
            return dispatch(ReviewsActions.updateResponse(params));
        }
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.CREATE_REVIEW_RESPONSE));
        return dispatch(ReviewsActions.createResponse(params));
    }

    handleReportReview(reviewId, reviewType, reason, comment = '') {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.REPORT_REVIEW, {
            label: reason,
        }));
        return dispatch(ReviewsActions.reportReview({
            reviewId, reviewType, reason, comment,
        }));
    }

    handleDeleteResponse(reviewResponseId, reviewType) {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DELETE_REVIEW_RESPONSE));
        return dispatch(ReviewsActions.deleteResponse({ reviewResponseId, reviewType }));
    }

    handleSeeMore() {
        const { reviewsForListing } = this.props;
        const { totalNumberOfReviews } = reviewsForListing;
        const nbrReviews = reviewsForListing.reviews.length;

        // if on last page
        if (nbrReviews >= totalNumberOfReviews) {
            return;
        }

        this.getReviews(nbrReviews, true);
    }

    getReviews(start = 0, appendToReviews = false) {
        const {
            sort,
            hideReviewsWithResponses,
            isLoadingReviews,
            searchText,
        } = this.state;
        const { dispatch, match } = this.props;
        const params = {
            listingId: match.params.propertyId,
            limit: 10,
            start,
            searchText,
            sort,
            hideReviewsWithResponses,
        };

        if (!isLoadingReviews) {
            this.setState({ isLoadingReviews: true });
        }

        return dispatch(ReviewsActions.getReviewsForListing(params, appendToReviews))
            .then((result) => {
                if (result.success === false) {
                    this.setState({
                        isLoadingReviews: false,
                        error: ErrorMessageConstants.DEFAULT_ERROR,
                    });
                } else if (isLoadingReviews) {
                    this.setState({
                        isLoadingReviews: false,
                        error: '',
                    });
                }
            }).catch(() => {
                this.setState({
                    isLoadingReviews: false,
                    error: ErrorMessageConstants.DEFAULT_ERROR,
                });
            });
    }

    getReviewNotifications() {
        const { dispatch } = this.props;
        this.setState({ isLoadingReviewNotifications: true });
        return dispatch(NotificationActions.getSettings())
            .then(() => this.setState({ isLoadingReviewNotifications: false }))
            .catch(() => this.setState({ isLoadingReviewNotifications: false }));
    }

    getTotalText(totalNumberOfReviews, averageStarLevel) {
        let text = 'No Review';
        const hasReviews = totalNumberOfReviews > 0;
        if (hasReviews) {
            const textItems = [
                stringHelper.pluralize(totalNumberOfReviews, 'review', 'reviews'),
            ];

            if (averageStarLevel > 0) {
                textItems.push(`${averageStarLevel.toFixed(1)} average`);
            }

            text = `(${textItems.join(', ')})`;
        }

        return <Text className="ReviewsPage-ratings-text" type="body" uppercase={!hasReviews}>{text}</Text>;
    }

    getReviewNotificationOptedOut() {
        const { notificationSettings } = this.props;
        return notificationSettings
            .some(setting => setting.typeGroup === NotificationSettingsConstants.TYPE_GROUP.RM_REVIEW);
    }

    renderHeader() {
        const { brand } = this.context;
        const {
            isLoadingReviews, isLoadingReviewNotifications, sort, hideReviewsWithResponses,
        } = this.state;
        const { reviewsForListing } = this.props;
        const { totalNumberOfReviews, averageStarLevel } = reviewsForListing;
        const reviewNotificationOptedOut = this.getReviewNotificationOptedOut();

        return (
            <div>
                <Row>
                    <Col>
                        <div className="ReviewsPage-header-element">
                            <Text className="ReviewsPage-title" type="h2">Reviews</Text>
                        </div>
                        {!isLoadingReviews && (
                            <div className="ReviewsPage-header-element">
                                {totalNumberOfReviews > 0 && (
                                    <StarRating
                                        size={20}
                                        stars={5}
                                        initialStarValue={averageStarLevel}
                                    />
                                )}
                            </div>
                        )}
                        {!isLoadingReviews && (
                            <div className="ReviewsPage-header-element">
                                {this.getTotalText(totalNumberOfReviews, averageStarLevel)}
                            </div>
                        )}
                    </Col>
                </Row>
                {brand === Constants.UNFORMATTED_BRAND_NAMES.zillow && (
                    <Row noPadding={true}>
                        <Col>
                            <Checkbox
                                size="md"
                                label="Notify me when new reviews are posted"
                                disabled={isLoadingReviewNotifications}
                                checked={!reviewNotificationOptedOut}
                                onChange={this.handleReviewNotificationToggle}
                            />
                        </Col>
                    </Row>
                )}
                <Row className="ReviewsPage-search">
                    <Col md={12} lg={4} xl={3}>
                        <SearchBox
                            placeholder="Search reviews"
                            onSearch={this.handleSearch}
                        />
                    </Col>
                    <Col md={12} lg={5} xl={6}>
                        <div className="ReviewsPage-checkbox">
                            <Checkbox
                                size="md"
                                label="Hide reviews with response"
                                checked={hideReviewsWithResponses}
                                onChange={this.handleHideReviewsWithResponsesToggle}
                            />
                        </div>
                    </Col>
                    <Col md={12} lg={3} xl={3}>
                        <Select
                            name="sort"
                            value={sort}
                            onChange={this.handleSortChange}
                            disabled={isLoadingReviewNotifications}
                        >
                            <option value={SORT_VALUE_MAP.CREATED}>Date (new)</option>
                            <option value={SORT_VALUE_MAP.RATING}>Rating (highest)</option>
                        </Select>
                    </Col>
                </Row>
            </div>
        );
    }

    renderError() {
        const { reviewsForListing } = this.props;
        const { error } = this.state;
        let message;
        if (error) {
            message = error;
        } else if (reviewsForListing.reviews.length === 0) {
            message = 'There were no reviews found for this listing.';
        }

        return message ? <Text danger={error !== ''}>{message}</Text> : null;
    }

    renderContent() {
        const { user, reviewsForListing } = this.props;
        const canRespondToReviews = user.features.canRespondToReview;
        return (
            <div>
                {this.renderError()}
                {reviewsForListing.reviews.map(review => (
                    <Row key={`${review.reviewType}-${review.id}`}>
                        <Col>
                            <Review
                                review={review}
                                onSubmitResponse={this.handleSubmitResponse}
                                onReportReview={this.handleReportReview}
                                canEdit={canRespondToReviews}
                            />
                            {review.responseMessage && (
                                <ReviewResponse
                                    review={review}
                                    onSubmitResponse={this.handleSubmitResponse}
                                    onDeleteResponse={this.handleDeleteResponse}
                                    canEdit={canRespondToReviews}
                                />
                            )}
                        </Col>
                    </Row>
                ))}
                {reviewsForListing.reviews.length > 0 &&
                    <VisibilitySensor onChange={this.handleSeeMore} />
                }
            </div>
        );
    }

    render() {
        const { isLoadingReviews } = this.state;
        return (
            <div className="ReviewsPage">
                <HeadWrapper title="Reviews" />
                <Container className="ReviewsPage-Container">
                    {this.renderHeader()}
                    {!isLoadingReviews && this.renderContent()}
                </Container>
                {isLoadingReviews && <PDPContentSkeleton />}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    reviewsForListing: ReviewsSelectors.makeReviewsForListing(state),
    notificationSettings: NotificationSelectors.makeSettings(state),
});

ReviewsPage.propTypes = propTypes;

export { ReviewsPage };
export default connect(mapStateToProps)(ReviewsPage);
